import React, { useMemo } from 'react';
import {
  TalentMatchingTabContextProvider,
  type TalentMatchingTabContextValue,
} from 'talent_matching/contexts/talent_matching_tab_context';
import FilterForm from 'talent_matching/components/filter_form';
import { RankingInProgressProvider } from 'talent_matching/contexts/ranking_in_progress_context';
import { IndexingStatusProvider } from 'talent_matching/contexts/indexing_status_context';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useLocalStorage } from 'react-use';
import { TalentMatchingFilters } from 'talent_matching/types';

type RankingTrigger = 'background' | 'calibration';

type Props = Omit<
  TalentMatchingTabContextValue,
  'activeSidePanelTab' | 'setActiveSidePanelTab' | 'setSavedSearchOptions'
> & {
  initialFilters: Partial<TalentMatchingFilters>;
  previouslyCalibrated: boolean;
  rankingTrigger: RankingTrigger | null;
  pollIndexingOnLoad: boolean;
};

const TalentMatching = (talentMatchingProps: Props) => {
  const {
    initialFilters,
    previouslyCalibrated,
    rankingTrigger,
    pollIndexingOnLoad,
    savedSearchOptions: initialSavedSearchOptions,
    ...stableContextProps
  } = talentMatchingProps;

  const [activeSidePanelTab, setActiveSidePanelTab] = useLocalStorage<
    string | null
  >('talent_matching_candidate_panel_tab', null);

  const [savedSearchOptions, setSavedSearchOptions] = React.useState(
    initialSavedSearchOptions
  );

  const contextValue = useMemo(
    () => ({
      ...stableContextProps,
      activeSidePanelTab: activeSidePanelTab ?? null,
      setActiveSidePanelTab,
      savedSearchOptions,
      setSavedSearchOptions,
    }),
    [
      stableContextProps,
      activeSidePanelTab,
      setActiveSidePanelTab,
      savedSearchOptions,
    ]
  );

  return (
    <TalentMatchingTabContextProvider value={contextValue}>
      <IndexingStatusProvider pollOnLoad={pollIndexingOnLoad}>
        <RankingInProgressProvider
          rankingTrigger={rankingTrigger}
          previouslyCalibrated={previouslyCalibrated}
        >
          <FilterForm initialFilters={initialFilters} />
          <ReactQueryDevtools initialIsOpen={false} />
        </RankingInProgressProvider>
      </IndexingStatusProvider>
    </TalentMatchingTabContextProvider>
  );
};

export default TalentMatching;
